import { useCallback, useState } from "react";

import type { FormikHelpers } from "formik";

import type { FormikInitialValuesNewRequest } from "../../model/form.model";
import {
  AvailableDatesResponse,
  CreateOrderResultUI,
} from "../../model/order.model";

import { useNewRequestContext } from "../../state/NewRequestProvider";

import { toPurchaseAutofill } from "../../application/mappers/toPurchaseAutofill";
import { toCustomerMapper } from "../../application/mappers/toCustomer";
import { submitCreateOrder } from "../../application/usecases/orders/submitCreateOrder";

import { useFlags } from "flagsmith/react";
import { USER_FLAGS_ENUM } from "@utils/featureFlags";
import useDialogAlert from "@hooks/useDialogAlert";

export function useNewRequestController() {
  const { snackbar } = useDialogAlert();

  const flag = useFlags([USER_FLAGS_ENUM.USER_CREATE_ORDER_V2]);

  const { services, state, dispatch } = useNewRequestContext();

  const purchase = state.purchase;

  const isLoading = purchase.loadingCustomer || purchase.loadingDates;

  const [createResult, setCreateResult] = useState<CreateOrderResultUI | null>(
    null
  );

  const selectCustomer = useCallback(
    async (
      cnpj: string,
      formik: FormikHelpers<FormikInitialValuesNewRequest> & {
        values: FormikInitialValuesNewRequest;
      }
    ) => {
      formik.resetForm();
      dispatch({ type: "purchase/reset" });

      dispatch({ type: "purchase/setError", value: undefined });
      dispatch({ type: "purchase/loadingCustomer", value: true });

      try {
        const infoClient = await services.clientInfoService.execute(cnpj);

        const customer = toCustomerMapper(infoClient);
        dispatch({ type: "purchase/setCustomer", value: customer });

        const lastPayments = await services.lastPaymentService.execute(cnpj);
        dispatch({ type: "purchase/setLastPayment", value: lastPayments });

        // autofill
        const patch = toPurchaseAutofill(customer);
        for (const [k, v] of Object.entries(patch)) {
          formik.setFieldValue(`purchase.${k}`, v, false);
        }

        const addrSelected =
          customer.addresses.find(
            (a) => String(a.id) === String(patch.deliveryAddressId ?? "")
          ) ?? customer.addresses[0];

        // Dados do responsável
        formik.setFieldValue(
          "purchase.responsibleName",
          customer.accessor?.name ?? "",
          false
        );
        formik.setFieldValue(
          "purchase.responsiblePhone",
          addrSelected?.phone ?? "",
          false
        );
        formik.setFieldValue(
          "purchase.responsibleEmail",
          addrSelected?.email ?? "",
          false
        );

        const withdrawBasisID = String(patch.withdrawBasisId ?? "");
        if (!withdrawBasisID) {
          dispatch({ type: "purchase/setDates", value: null });
          formik.setFieldValue("purchase.deliveryDate", "", false);
          return;
        }

        dispatch({ type: "purchase/loadingDates", value: true });

        const datesMap = await services.clientInfoService.availableDates({
          CNPJ: customer.cnpj,
          freightType: customer.freightType,
          withdrawBasisID,
        });

        dispatch({ type: "purchase/setDates", value: datesMap });
        const first = firstAllowedDate(datesMap);
        formik.setFieldValue(
          "purchase.deliveryDate",
          first ? `${first}T00:00:00` : "",
          false
        );
      } catch {
        dispatch({
          type: "purchase/setError",
          value: "Falha ao carregar dados da compra.",
        });

        dispatch({ type: "purchase/setCustomer", value: undefined });
        dispatch({ type: "purchase/setDates", value: null });
        formik.resetForm();
      } finally {
        dispatch({ type: "purchase/loadingCustomer", value: false });
        dispatch({ type: "purchase/loadingDates", value: false });
      }
    },
    [dispatch, services]
  );

  const reloadDates = useCallback(
    async (args: {
      withdrawBasisID: string;
      freightType: "CIF" | "FOB";
      formik: FormikHelpers<FormikInitialValuesNewRequest>;
    }) => {
      const customer = purchase.customer;
      if (!customer) return;

      dispatch({ type: "purchase/setError", value: undefined });
      dispatch({ type: "purchase/loadingDates", value: true });

      try {
        const datesMap = await services.clientInfoService.availableDates({
          CNPJ: customer.cnpj,
          freightType: args.freightType,
          withdrawBasisID: args.withdrawBasisID,
        });

        dispatch({ type: "purchase/setDates", value: datesMap });

        const first = firstAllowedDate(datesMap);
        args.formik.setFieldValue(
          "purchase.deliveryDate",
          first ? `${first}T00:00:00` : "",
          false
        );
      } catch {
        dispatch({
          type: "purchase/setError",
          value: "Falha ao carregar datas disponíveis.",
        });
        dispatch({ type: "purchase/setDates", value: null });
        args.formik.setFieldValue("purchase.deliveryDate", "", false);
      } finally {
        dispatch({ type: "purchase/loadingDates", value: false });
      }
    },
    [dispatch, services, purchase.customer]
  );

  const resetPurchase = useCallback(
    (formik: FormikHelpers<FormikInitialValuesNewRequest>) => {
      dispatch({ type: "purchase/reset" });

      // zera o form
      formik.setFieldValue("purchase.customerCnpj", "", false);
      formik.setFieldValue("purchase.customerName", "", false);
      formik.setFieldValue("purchase.deliveryAddressId", "", false);
      formik.setFieldValue("purchase.postalCode", "", false);
      formik.setFieldValue("purchase.district", "", false);
      formik.setFieldValue("purchase.cityState", "", false);
      formik.setFieldValue("purchase.withdrawBasisId", "", false);
      formik.setFieldValue("purchase.branchName", "", false);
      formik.setFieldValue("purchase.deliveryDate", "", false);
      formik.setFieldValue("purchase.freightType", "CIF", false);

      formik.setFieldValue("purchase.responsibleName", "", false);
      formik.setFieldValue("purchase.responsiblePhone", "", false);
      formik.setFieldValue("purchase.responsibleEmail", "", false);
    },
    [dispatch]
  );

  const submitOrder = useCallback(
    async (values: FormikInitialValuesNewRequest) => {
      dispatch({ type: "purchase/setError", value: undefined });
      dispatch({ type: "products/setError", value: undefined });

      const result = await submitCreateOrder({
        services,
        state,
        values,
        useV2: flag.user_create_order_v2.enabled ?? false,
      });

      if (!result.ok) {
        dispatch({ type: "products/setError", value: result?.reason });
        snackbar({ message: result.reason ?? "", variant: "error" });
      }

      setCreateResult(result);

      return result;
    },

    [services, state, flag, snackbar, dispatch]
  );

  const clearCreateResult = useCallback(() => {
    setCreateResult(null);
  }, []);

  const resetFlow = useCallback(
    (formik: FormikHelpers<FormikInitialValuesNewRequest>) => {
      setCreateResult(null);
      dispatch({ type: "reset/all" });
      formik.resetForm();
    },
    [dispatch]
  );

  return {
    state: {
      cart: state.products.cart,
      purchase,
      isLoading,

      createResult,
    },
    action: {
      clearCreateResult,
      resetFlow,

      selectCustomer,
      reloadDates,
      resetPurchase,
      submitOrder,
    },
  };
}

function firstAllowedDate(
  map: Record<string, boolean> | AvailableDatesResponse
): string {
  return (
    Object.entries(map)
      .filter(([, ok]) => ok === true)
      .map(([d]) => d)
      .sort()[0] ?? ""
  );
}
